<template>
    <mini-calculator-page>
        <div slot="summary-panel" :class="{'flex-grid--vertical' : !isValid}">
            <div class="p-animated-calc__results a-form-inputs u-full-width u-margin-auto">
                <mini-calculator-header title="Inflation Calculator" icon="inflation-calculator"/>

                <control for="amount" label="Amount" before-after="pound">
                    <input autocomplete="off" v-model="amount" ref="amount" min="1"
                           before-after="pound"
                           v-on:focus="amount = amount || ''"
                           v-on:blur="amount = amount || 0"
                           v-two-dp
                           type="text">
                    <control-error :show="$error('amount')">
                        The amount must be at least &pound;1.00
                    </control-error>
                </control>

                <control label="Date From">
                    <div class="a-form-inputs a-form-inputs--half-calcs">
                        <div class="a-form-inputs__container">
                            <select v-model="minYear">
                                <option :value="null" disabled hidden>Year</option>
                                <option v-for="year in rpiYears" :value="year">{{ year }}</option>
                            </select>
                        </div>
                        <div class="a-form-inputs__container">
                            <select v-model="minMonth">
                                <option :value="null" disabled hidden>Month</option>
                                <option v-for="month in 12" :value="month">
                                    {{ monthToString(month) }}
                                </option>
                            </select>
                        </div>
                    </div>
                </control>

                <control label="Date To">
                    <div class="a-form-inputs a-form-inputs--half-calcs">
                        <div class="a-form-inputs__container">
                            <select v-model="maxYear">
                                <option :value="null" disabled hidden>Year</option>
                                <option v-for="year in toYears" :value="year">{{ year }}</option>
                            </select>
                        </div>
                        <div class="a-form-inputs__container">
                            <select v-model="maxMonth">
                                <option :value="null" disabled hidden>Month</option>
                                <option v-for="month in toMonths" :value="month">
                                    {{ monthToString(month) }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <control-error v-show="toMonths.length === 0">
                        Date To must be after Date From
                    </control-error>
                </control>

                <div v-show="isValid">
                    <h2>
                        Result
                    </h2>

                    <control :label="monthToString(minMonth) + ' ' + minYear" before-after="pound-on-dark">
                        <input autocomplete="off" :value="currencyFormat(amount, '')"
                               disabled
                               type="text">
                    </control>

                    <control :label="monthToString(maxMonth) + ' ' + maxYear" before-after="pound-on-dark">
                        <input autocomplete="off" :value="currencyFormat(equivalent, '')"
                               disabled
                               type="text">
                    </control>

                    <control label="Difference" before-after="pound-on-dark">
                        <input autocomplete="off" :value="currencyFormat(difference, '')"
                               disabled
                               type="text">
                    </control>
                    <control label="Difference" before-after="percent-on-dark">
                        <input autocomplete="off" :value="percentageDifference(amount, equivalent)"
                               disabled
                               v-two-dp
                               type="text">
                    </control>
                </div>
            </div>
        </div>
        <div slot="results-panel">
            <div class="p-animated-calc__results-panel u-full-width u-margin-auto">
                <h2>Explanation</h2>

                <p>
                    The inflation calculator can be used to show how the cost of goods and services
                    has changed over time.
                </p>

                <p>
                    Using the Retail Price Index (RPI) data sourced from the Office For National Statistics (ONS),
                    the calculator has determined that
                    {{ currencyFormat(amount) }} in {{ monthToString(minMonth) }} {{ minYear }}
                    would be the equivalent of
                    {{ currencyFormat(equivalent) }} in {{ monthToString(maxMonth) }} {{ maxYear }}.
                </p>

                <p>
                    That's a difference of {{ currencyFormat(difference)}}
                    ({{ roundValue(percentageDifference(equivalent, amount), 2) }}%)
                    during the period of {{ startToEnd }}.<br><br>
                </p>

                <h3>
                    Visual comparison of inflation applied to {{ currencyFormat(amount) }}
                </h3>

                <br>

                <value-bars :values="barValues">
                    <h3>{{ monthToString(minMonth) }} {{ minYear }}</h3>
                    <div class="m-bars__bar bar1"><span></span></div>
                    <h3>{{ monthToString(maxMonth) }} {{ maxYear }}</h3>
                    <div class="m-bars__bar bar2"><span></span></div>
                </value-bars>

                <br>

                <h2>Calculation</h2>

                <p>
                    The value of inflation was calculated using the following formula:
                </p>

                <div class="m-formula">
                    <div class="m-formula__item--row">
                        <strong>
                            ([£ Value] / [RPI @ Start]) x [RPI @ End] = [£ Value at End]
                        </strong>
                    </div>
                </div>

                <p>
                    Which was populated with the RPI data sourced from the ONS to determine the value:
                </p>

                <div class="m-formula u-margin-bottom-20">
                    <div class="m-formula__item--row">
                        <strong>
                            ( {{ currencyFormat(amount)}} / {{ getRpiForDate(minYear, minMonth)
                            }} ) x {{ getRpiForDate(maxYear, maxMonth) }} = {{ currencyFormat(equivalent)}}
                        </strong>
                    </div>
                </div>
            </div>
        </div>
    </mini-calculator-page>
</template>

<script type="text/babel">
    import _ from 'lodash';
    import {mapGetters, mapActions} from 'vuex';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import MiniCalculatorPage from 'pages/mini-calculators/components/MiniCalculatorPage';
    import miniCalcIcons from 'components/Icons.vue';
    import Intro from 'pages/mini-calculators/calculators/CalculatorComponents/Intro';
    import DateInput from 'components/form/control/DateInput.vue';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import CalculatorKeyBindings from 'pages/mini-calculators/calculators/mixins/MiniCalculatorBindings';
    import currencyFormat from 'mixins/currencyFormat';
    import percentageDifference from 'mixins/percentageDifference';
    import roundValue from 'mixins/roundValue';
    import MiniCalculatorHeader from 'pages/mini-calculators/components/MiniCalculatorHeader';
    import moment from 'moment';
    import ValueBars from 'components/form/ValueBars';

    export default {
        name: 'inflation-calculator',
        created() {
            this.resetForm();
        },
        computed: {
            rpi() {
                return this.findValue('rpiData');
            },
            amount: {
                get() {
                    return this.findValue('amount');
                },
                set(value) {
                    this.updateValue({key: 'amount', value});
                }
            },
            minYear: {
                get() {
                    return this.findValue('minYear');
                },
                set(value) {
                    this.updateValue({key: 'minYear', value});
                }
            },
            minMonth: {
                get() {
                    return this.findValue('minMonth');
                },
                set(value) {
                    this.updateValue({key: 'minMonth', value});
                }
            },
            maxYear: {
                get() {
                    return this.findValue('maxYear');
                },
                set(value) {
                    this.updateValue({key: 'maxYear', value});
                }
            },
            maxMonth: {
                get() {
                    return this.findValue('maxMonth');
                },
                set(value) {
                    this.updateValue({key: 'maxMonth', value});
                }
            },
            rpiYears() {
                let data = this.findValue('rpiData');

                if (!data.length) {
                    return [];
                }
                return _(data)
                    .map(item => item.year)
                    .uniq()
                    .reverse()
                    .value();
            },
            rpiMonths() {
                let data = this.findValue('rpiData');

                if (!data.length) {
                    return [];
                }

                return _(data)
                    .filter(item => item.year === this.maxYear)
                    .map(item => item.month)
                    .value();
            },
            toYears() {
                if (!this.minYear) {
                    return this.rpiYears;
                }
                return _(this.rpiYears)
                    .filter(year => year >= this.minYear)
                    .value();
            },
            toMonths() {
                let now = moment();
                let months = this.rpiMonths;
                if (!this.minMonth || this.minYear !== this.maxYear) {
                    return 12;
                }

                if (this.maxYear === now.year()) {
                    months.splice(now.month() - 1);
                }

                return _(months)
                    .filter(month => month > this.minMonth)
                    .value();
            },
            fromRpi() {
                return this.getRpiForDate(this.minYear, this.minMonth);
            },
            toRpi() {
                return this.getRpiForDate(this.maxYear, this.maxMonth);
            },
            equivalent() {
                return (this.amount / this.fromRpi * this.toRpi);
            },
            startToEnd() {
                let start = moment(this.minYear + '-' + this.minMonth, 'YYYY-M');
                let end = moment(this.maxYear + '-' + this.maxMonth, 'YYYY-M');

                return moment.duration(end.diff(start))
                    .format('y[ years] [and] M[ months]').replace(" and 0 months", "");
            },
            barValues() {
                return [
                    this.twoDp(this.amount),
                    this.twoDp(this.equivalent),
                ];
            },
            difference() {
                return this.equivalent - this.amount;
            },
            isValid() {
                return (
                    this.amount > 0
                    && this.minMonth !== null
                    && this.minYear !== null
                    && this.maxMonth !== null
                    && this.maxYear !== null
                );
            }
        },
        mixins: [
            FormValidator,
            currencyFormat,
            percentageDifference,
            roundValue,
            CalculatorKeyBindings
        ],
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        components: {
            Control,
            ControlError,
            DateInput,
            MiniCalculatorPage,
            MiniCalculatorHeader,
            miniCalcIcons,
            Intro,
            ValueBars,
        },
        mounted: function () {
            this.loadRpiData();
        },
        watch:{
            isValid(valid) {
                if (valid) {
                    this.hidePrompt();
                } else {
                    this.showPrompt();
                }
            }
        },
        methods: {
            ...mapActions('mini/inflation', {
                loadRpiData: 'loadRpiData',
                updateValue: 'updateValue',
                resetForm: 'reset'
            }),
            twoDp(value) {
                return (Math.round(value * 100) / 100).toFixed(2);
            },
            monthToString(value) {
                return moment(value, 'M').format('MMMM');
            },
            getRpiForDate(year, month) {
                let data = this.findValue('rpiData');
                const match = _(data)
                    .find(item => item.year === year && item.month === month);
                if (match) {
                    return match.amount;
                }
                return 0;
            },
            findValue(key) {
                return this.$store.getters['mini/inflation/' + key];
            }
        }
    }
</script>



// WEBPACK FOOTER //
// inflationCalculator.vue?db75506e